import { useNotifications } from '@armhr/ui';
import {
  AssignmentLateOutlined,
  AssignmentTurnedInOutlined,
  DownloadOutlined,
  EditOutlined,
  ExpandMore,
  PendingActionsOutlined,
  PersonRemoveOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRowsProp,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { useMemo, useState } from 'react';

import { useApi } from '../../hooks/useApi';
import { useApiData } from '../../hooks/useApiData';
import { formatKeyToHandbookTitle } from '../../utils/handbooks';
import { type EditAssignmentRow } from './EditAssignmentModal';

interface AdminHandbookProgressBarProps {
  handbook: HandbookAsset;
  assignments: EmployeeHandbookAssignment[];
  onEditAssignment: (row: EditAssignmentRow) => void;
  onUnassignAssignment: (
    assignmentId: number,
    handbookId: number,
    employeeName: string
  ) => void;
}

const AdminHandbookProgressBar: React.FC<AdminHandbookProgressBarProps> = ({
  handbook,
  assignments,
  onEditAssignment,
  onUnassignAssignment,
}) => {
  const api = useApi();
  const { showNotification } = useNotifications();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const { data: employees, loading: eLoading } = useApiData((api) =>
    api.company.getEmployees()
  );

  const handleDownloadSigned = async (row: EditAssignmentRow) => {
    if (!row.id) return;
    setDownloadingId(row.id);
    try {
      const blob = await api.company.downloadSignedHandbook(row.id);
      const employeeName = [row.first_name, row.middle_initial, row.last_name]
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, '_');
      const handbookTitle = formatKeyToHandbookTitle(
        handbook.key ?? ''
      ).replace(/\.pdf$/i, '');
      const filename = `${handbookTitle || 'handbook'}-${
        employeeName || 'employee'
      }-signed.pdf`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      showNotification({
        severity: 'error',
        message: 'Failed to download the signed handbook. Please try again.',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const employeeAssignments = useMemo(() => {
    if (!assignments || !employees) return [];
    return assignments
      .map((a) => {
        const match = employees.find((e) => e.id === a.employee_id);
        return match
          ? {
              first_name: match.first_name,
              last_name: match.last_name,
              middle_initial: match.middle_initial,
              ...a,
            }
          : null;
      })
      .filter((item) => item !== null);
  }, [assignments, employees]);

  const progress =
    employeeAssignments.length > 0
      ? (100 *
          employeeAssignments.filter((a) =>
            ['signed', 'read_only'].includes(a.status)
          ).length) /
        employeeAssignments.length
      : null;

  const hasInProgress = useMemo(
    () =>
      assignments?.some(
        (a) => a.status === 'pending' || a.status === 'overdue'
      ) ?? false,
    [assignments]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'first_name',
        headerName: 'First name',
        flex: 1,
      },
      {
        field: 'middle_initial',
        headerName: 'MI',
        width: 60,
        sortable: false,
      },
      {
        field: 'last_name',
        headerName: 'Last name',
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        renderCell: (params) => {
          if (params.value === 'signed')
            return (
              <Tooltip title="Completed" placement="right">
                <AssignmentTurnedInOutlined color="success" fontSize="medium" />
              </Tooltip>
            );
          else if (params.value === 'overdue')
            return (
              <Tooltip title="Overdue" placement="right">
                <AssignmentLateOutlined color="error" fontSize="medium" />
              </Tooltip>
            );
          else if (params.value === 'read_only')
            return (
              <Tooltip title="View only" placement="right">
                <VisibilityOutlined fontSize="medium" />
              </Tooltip>
            );
          else
            return (
              <Tooltip title="Pending" placement="right">
                <PendingActionsOutlined color="warning" fontSize="medium" />
              </Tooltip>
            );
        },
      },
      {
        field: 'assigned_at',
        headerName: 'Assigned date',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueFormatter: (params) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleDateString();
        },
      },
      {
        field: 'due_date',
        headerName: 'Due date',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        valueFormatter: (params) => {
          if (!params.value) return '---';
          return new Date(params.value).toLocaleDateString();
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        filterable: false,
        width: 140,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => {
          const row = params.row as EditAssignmentRow;
          return (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{ display: 'flex', gap: 0.5 }}
            >
              {row.status === 'signed' && (
                <Tooltip title="Download signed handbook">
                  <span>
                    <IconButton
                      size="small"
                      disabled={downloadingId === row.id}
                      onClick={() => handleDownloadSigned(row)}
                    >
                      {downloadingId === row.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DownloadOutlined fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Edit deadline">
                <IconButton size="small" onClick={() => onEditAssignment(row)}>
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Unassign">
                <IconButton
                  size="small"
                  onClick={() =>
                    onUnassignAssignment(
                      Number(row.id),
                      handbook.id,
                      [row.first_name, row.middle_initial, row.last_name]
                        .filter(Boolean)
                        .join(' ')
                    )
                  }
                >
                  <PersonRemoveOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handbook.id, onEditAssignment, onUnassignAssignment, downloadingId]
  );

  if (progress === null) return null;
  return (
    <Accordion
      defaultExpanded={hasInProgress}
      sx={{ backgroundColor: 'white' }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          '&.Mui-expanded': {
            borderBottom: 'none',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography variant="h6">
            {handbook.key.substring(
              handbook.key.lastIndexOf('/') + 1,
              handbook.key.lastIndexOf('.')
            )}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                mr: 1,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 8 }}
              />
            </Box>
            <Box sx={{ minWidth: 40, textAlign: 'left' }}>
              <Typography variant="body2">
                {`${Math.round(progress)}%`}
              </Typography>
            </Box>
          </Box>
        </Box>
      </AccordionSummary>
      {eLoading ? (
        <CircularProgress
          sx={{ color: 'primary.main', m: '0 auto', mt: 24, size: 50 }}
        />
      ) : (
        <AccordionDetails
          sx={{
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            maxHeight: '100%',
          }}
        >
          <DataGrid
            showColumnVerticalBorder
            columns={columns}
            rows={(employeeAssignments as GridRowsProp) || []}
            slots={{ toolbar: QuickSearchToolbar }}
            disableRowSelectionOnClick
            density="compact"
            sx={{
              height: '400px',
              background: 'white',
              fontSize: '0.875rem',
              '& .MuiDataGrid-columnHeader': {
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '&:last-child': {
                  borderRight: 'none',
                },
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-footerContainer': {
                height: 42,
                minHeight: 42,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
              },
              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:active':
                {
                  outline: 'none',
                },
            }}
          />
        </AccordionDetails>
      )}
    </Accordion>
  );
};

function QuickSearchToolbar() {
  return (
    <GridToolbarContainer sx={{ mb: 2 }}>
      <GridToolbarQuickFilter debounceMs={500} sx={{ marginLeft: 'auto' }} />
    </GridToolbarContainer>
  );
}

export default AdminHandbookProgressBar;
